import { colors } from "@areahub/theme";
import {
  Box,
  Divider,
  Flex,
  HStack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Button, ButtonLink_T, Button_T } from "@ui/components/button";
import { Link, Link_T } from "@ui/components/link";
import { usePathname, useRouter } from "next/navigation";
import { Fragment, PropsWithChildren } from "react";
import { DefaultMobileMenu } from "./default-mobile-menu";
import { HamburgerButton } from "./hamburger-button";
import { LinkedLogo } from "./linked-logo";
import { PremiumMobileMenu } from "./premium/premium-mobile-menu";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";
import { getWebUrl } from "@areahub/config";
import { IndustriesDropdownMenu } from "./industries-dropdown-menu";
import { isBusinesses } from "@ui/utils";

export interface IndustriesMenu {
  buttonLabel: string;
  industriesMenuItem: {
    option: string;
    url: string;
  }[];
  onToggle(): void;
}

export interface LinkWithLabel_T extends Link_T {
  label: string;
  isInternal?: boolean;
  industriesMenu?: IndustriesMenu;
}

export interface NavContent_T extends PropsWithChildren<{}> {
  buttons?: Array<Button_T & { isInternal?: boolean }>;
  links?: LinkWithLabel_T[];
  mobileLinks?: LinkWithLabel_T[];
  navBarToggle?: {
    links: LinkWithLabel_T[];
    buttons: Array<Button_T & { isInternal?: boolean }>;
  };
}

const MobileNavContent = ({
  buttons,
  links,
  children,
  mobileLinks,
  navBarToggle,
}: NavContent_T) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const pathname = usePathname();
  return (
    <>
      {/* Main navigational bar */}
      <Flex
        align="center"
        justify="space-between"
        className="nav-content__mobile"
        display={{
          base: "flex",
          lg: "none",
        }}
        p="10px"
      >
        <LinkedLogo h="30px" />
        {process.env.APP_NAME !== "PREMIUM" ? (
          <Flex
            key="navbar-toggle-wrapper"
            gap={5}
            color={
              isBusinesses(pathname as string)
                ? colors.enterpriseColors.blue["300"]
                : colors.brand[1000]
            }
          >
            {navBarToggle?.links.map(({ isInternal, ...link }, idx) => (
              <Link
                key={`${link.href ?? "navbar-link"}-${idx}`}
                href={`${
                  process.env.APP_NAME === "REPORTS" ? getWebUrl() : ""
                }${link.href}`}
                fontWeight={
                  (process.env.APP_NAME === "REPORTS" &&
                    link.label === "Individuals") ||
                  (pathname?.includes("individuals") &&
                    (link.href as string).includes("individuals")) ||
                  (isBusinesses(pathname as string) &&
                    isBusinesses(link.href as string))
                    ? "bold"
                    : ""
                }
                borderBottom={
                  (process.env.APP_NAME === "REPORTS" &&
                    link.label === "Individuals") ||
                  (pathname?.includes("individuals") &&
                    (link.href as string).includes("individuals")) ||
                  (isBusinesses(pathname as string) &&
                    isBusinesses(link.href as string))
                    ? `3px solid ${
                        isBusinesses(pathname as string)
                          ? colors.enterpriseColors.blue["300"]
                          : colors.brand[1000]
                      }`
                    : ""
                }
                _hover={{
                  textDecor: "none",
                }}
              >
                {link.label}
              </Link>
            ))}
          </Flex>
        ) : (
          <></>
        )}
        <HamburgerButton isOpen={isOpen} onClick={onToggle} />
      </Flex>

      {process.env.APP_NAME !== "PREMIUM" ? (
        <>
          <DefaultMobileMenu
            isOpen={isOpen}
            onClose={onClose}
            mobileLinks={mobileLinks as LinkWithLabel_T[]}
            onToggle={onToggle}
          />
        </>
      ) : (
        <>
          <PremiumMobileMenu
            isOpen={isOpen}
            onClose={onClose}
            children={children}
            links={links as LinkWithLabel_T[]}
            buttons={buttons as Array<Button_T & { isInternal?: boolean }>}
          />
        </>
      )}
    </>
  );
};

const DesktopNavContent = ({
  buttons,
  links,
  children,
  navBarToggle,
}: NavContent_T) => {
  const pathname = usePathname();
  const router = useRouter();
  const analytics = useAnalytics();

  return (
    <Flex flexDir={"column"}>
      {process.env.APP_NAME !== "PREMIUM" ? (
        <Flex
          key="top-level-navbar"
          alignItems={"center"}
          borderBottom={
            isBusinesses(pathname as string)
              ? "none"
              : `2px solid ${colors.brand[1000]}`
          }
          bgColor={
            isBusinesses(pathname as string)
              ? colors.enterpriseColors.blue["300"]
              : "white"
          }
          color={
            isBusinesses(pathname as string) ? "white" : colors.brand[1000]
          }
          fontSize={"16px"}
          fontWeight={"normal"}
          h="35px"
          justifyContent={"space-between"}
          px={{
            lg: 10,
            xl: 16,
          }}
          display={{
            base: "none",
            lg: "flex",
          }}
        >
          <Flex key="navbar-toggle-wrapper" gap={3}>
            {navBarToggle?.links.map(({ isInternal, ...link }, idx) => (
              <Fragment key={`navbar-toggle-element-${idx}`}>
                {idx === navBarToggle.links.length - 1 ? (
                  <Divider
                    borderColor={
                      isBusinesses(pathname as string)
                        ? "white"
                        : colors.brand[1000]
                    }
                    borderWidth={1.5}
                    h="auto"
                    opacity={100}
                    orientation="vertical"
                  />
                ) : (
                  <Fragment></Fragment>
                )}
                <Link
                  fontWeight={
                    (pathname?.includes("individuals") &&
                      (link.href as string).includes("individuals")) ||
                    (isBusinesses(pathname as string) &&
                      isBusinesses(link.href as string))
                      ? "bold"
                      : ""
                  }
                  _hover={{
                    textDecor: "none",
                  }}
                  onClick={() => {
                    analytics.track("User clicked on B2B/B2C toggle", {
                      ui: "DesktopNavContent",
                      label: link.label,
                      requestedUrl: `${getWebUrl() + link.href}`,
                    });
                  }}
                  href={`${
                    process.env.APP_NAME === "REPORTS" ? getWebUrl() : ""
                  }${link.href}`}
                >
                  {link.label}
                </Link>
              </Fragment>
            ))}
          </Flex>
          {navBarToggle?.buttons.map((button, idx) => (
            <Button
              key={`navbar-buttons-${idx}`}
              color={
                isBusinesses(pathname as string) ? "white" : colors.brand[1000]
              }
              label={"Sign In"}
              bg="none"
              _hover={{
                bg: "none",
              }}
              px={0}
              {...button}
            />
          ))}
        </Flex>
      ) : (
        <Fragment key="top-level-fragment"></Fragment>
      )}
      <Flex
        className="nav-content__desktop"
        key="content__desktop"
        align="center"
        justify="space-between"
        display={{
          base: "none",
          lg: "flex",
        }}
        h="72px"
        px={{
          lg: 10,
          xl: 16,
        }}
      >
        <LinkedLogo h="40px" />
        <HStack
          as="ul"
          key="nav__primary-menu"
          id="nav__primary-menu"
          aria-label="Main Menu"
          listStyleType="none"
          alignItems="center"
          justifyContent="flex-end"
          fontWeight="semibold"
          fontSize="16px"
          gap={"30px"}
          pl={10}
        >
          {links?.map(({ label, isInternal, ...rest }, idx) => (
            <Flex
              as="li"
              key={`nav__primary-link-${idx}`}
              flexDir={"row"}
              align={"center"}
              h="72px"
              _hover={{
                borderBottom: isBusinesses(pathname as string)
                  ? `3px solid ${colors.enterpriseColors.blue["100"]}`
                  : "",
                textDecor: "none",
              }}
            >
              {isBusinesses(pathname as string) ? (
                label === "Industries" ? (
                  <IndustriesDropdownMenu
                    {...(rest.industriesMenu as IndustriesMenu)}
                  />
                ) : (
                  <Link
                    onClick={() => {
                      analytics.track(
                        "User clicked on a link on the B2B Desktop Nav",
                        {
                          ui: "DesktopNavContent",
                          label: label,
                          requestedUrl: `${getWebUrl() + rest.href}`,
                        },
                      );
                    }}
                    color={colors.enterpriseColors.blue["200"]}
                    _hover={{
                      textDecor: "none",
                    }}
                    {...rest}
                  >
                    {label}
                  </Link>
                )
              ) : (
                <Link
                  onClick={() => {
                    analytics.track(
                      "User clicked on a link on the B2C Desktop Nav",
                      {
                        ui: "DesktopNavContent",
                        label: label,
                        requestedUrl: `${getWebUrl() + rest.href}`,
                      },
                    );
                  }}
                  color="blueGray.800"
                  _hover={{
                    color: "blueGray.500",
                    textDecor: "none",
                  }}
                  {...rest}
                >
                  {label}
                </Link>
              )}
            </Flex>
          ))}
          {buttons?.map(({ isInternal, ...btn }, idx) => (
            <Box as="li" key={`nav__primary-button-${idx}`}>
              <Button
                {...btn}
                width="180px"
                fontSize={"unset"}
                py={0}
                bgColor={
                  isBusinesses(pathname as string)
                    ? colors.enterpriseColors.blue["200"]
                    : "green.700"
                }
                color={"white"}
                onClick={() => {
                  analytics.track(
                    `User clicked on a link on the ${
                      pathname === "/" ? "B2B" : "B2C"
                    } Desktop Nav`,
                    {
                      ui: "DesktopNavContent",
                      label: btn.label,
                      requestedUrl: `${
                        getWebUrl() + (btn as ButtonLink_T)?.href
                      }`,
                    },
                  );
                }}
                textTransform={
                  isBusinesses(pathname as string) ? "uppercase" : "unset"
                }
              />
            </Box>
          ))}
          {/* Add extra children, i.e premium menus here */}
          <Fragment>{children}</Fragment>
        </HStack>
      </Flex>
    </Flex>
  );
};

export const NavContent = {
  Mobile: MobileNavContent,
  Desktop: DesktopNavContent,
};
